import React, { FC, useState, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import Search from '@mui/icons-material/Search';

import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import RadioGroup from '../../../sharedModules/layout/RadioGroup/RadioGroup';

import axios from '../../../sharedModules/utils/axios.utils';

let radioOptions = [
  { label: 'Company Name', model: 'Customer', main_filter: 'name_cont', value: 0 },
  {
    label: 'Contact Name',
    model: 'Contact',
    main_filter: 'first_name_or_last_name_cont',
    value: 1,
  },
  {
    label: 'Address',
    model: 'Site',
    main_filter: 'address1_or_city_or_zip_or_address2_cont',
    value: 2,
  },
];

const CustomerLookupPage: FC = () => {
  let [customers, setCustomers] = useState([]);
  let [totalCustomerCount, setTotalCustomerCount] = useState();
  let [query, setQuery] = useState('');
  let [selectedSearchIndex, setSelectedSearchIndex] = useState<number>(0);
  let [selectedSearchType, setSelectedSearchType] = useState<any>();
  let [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setSelectedSearchType(radioOptions.find((elem) => elem.value === selectedSearchIndex));
  }, [selectedSearchIndex]);

  const customerColumns = [
    {
      name: 'Customer',
      selector: 'name',
      sortable: true,
    },
    {
      name: 'Assets',
      selector: 'asset_count',
      sortable: true,
      grow: 0.5,
    },
    {
      name: 'Contacts',
      cell: (row) => (
        <Box display="flex" flexDirection="column">
          {row.all_contacts?.map((contact) => {
            return (
              <Typography variant="body2" key={contact}>
                {contact}
              </Typography>
            );
          })}
        </Box>
      ),
    },
    {
      name: 'Addresses',
      grow: 1.5,
      cell: (row) => (
        <Box display="flex" flexDirection="column">
          {row.all_addresses?.map((address) => {
            return (
              <Typography variant="body2" key={address}>
                {address}
              </Typography>
            );
          })}
        </Box>
      ),
    },
  ];

  const fetchCustomers = async (searchQuery, page) => {
    setIsLoading(true);
    try {
      const response: any = await axios.get('/energy_solutions/energy_solutions', {
        params: {
          model_class: selectedSearchType?.model,
          [`ransack[${selectedSearchType?.main_filter}]`]: searchQuery,
          page,
        },
      });
      setCustomers(response.data.result);
      setTotalCustomerCount(response.data.total_records);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') fetchCustomers(query, 1);
  };

  const handlePageChange = (page) => {
    fetchCustomers(query, page);
  };

  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12}>
        <PageHeader mainTitle="Energy Solutions" subTitle="Customer Lookup" />
      </Grid>
      <Grid item xs={12} md={6}>
        <RadioGroup radioOptions={radioOptions} value={selectedSearchIndex} setValue={setSelectedSearchIndex} />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          variant="outlined"
          placeholder={`Search by ${selectedSearchType?.label}`}
          size="small"
          style={{ width: '100%', backgroundColor: 'white' }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <IconButton onClick={() => fetchCustomers(query, 1)} size="large">
                <Search />
              </IconButton>
            ),
          }}
        />
      </Grid>
      {/* Don't show table until we have customers, otherwise it triggers a seach upon initial page load. */}
      {customers.length > 0 && (
        <>
          <Grid item xs={12}>
            <Typography variant="h5">Results</Typography>
          </Grid>
          <Grid item xs={12}>
            <DataTable
              title=""
              progressPending={isLoading}
              columns={customerColumns}
              data={customers}
              onRowClicked={(row: any) => (window.location.href = `/energy_solutions/customers/${row.id}`)}
              striped
              dense
              highlightOnHover
              pointerOnHover
              pagination
              paginationServer
              paginationPerPage={10}
              paginationTotalRows={totalCustomerCount}
              noHeader
              onChangePage={handlePageChange}
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default CustomerLookupPage;
